import { AnalyticsFilters } from '@superfunnel/types';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import {
  getAdsAccountsUsage,
  queryAnalytics,
} from '../../services/AnalyticsService';
import { ChartDefinition } from '../../types/overview';
import { analyticsRangeToDates, formatCountryLabel } from '../../utils';
import {
  AreaChart,
  ChartCard,
  HorizontalBarChart,
  LineChart,
  MetricCard,
  PieChart,
  VerticalBarChart,
} from '../charts';

const safeGetAdsAccountsUsage = async (
  accountId: string,
  startDate: string,
  endDate: string,
  aggregateBy: 'date' | 'total',
) => {
  try {
    return await getAdsAccountsUsage(
      accountId,
      startDate,
      endDate,
      aggregateBy,
    );
  } catch (error) {
    console.error('Error fetching ads accounts usage:', error);
    return [];
  }
};

interface ChartRendererProps {
  definition: ChartDefinition;
  analyticsFilters: AnalyticsFilters;
  withoutCard?: boolean;
  chartClassName?: string;
  campaignId?: string;
  translateSegmentLabel?: (
    segment: string | undefined,
    value: string,
  ) => string;
}

export default function ChartRenderer({
  definition,
  analyticsFilters,
  withoutCard = false,
  chartClassName = '',
  campaignId,
  translateSegmentLabel: extraTranslateSegmentLabel,
}: ChartRendererProps) {
  const { selectedAccount } = useContext(UserContext);
  const { analyticsRange, dynamicPages, forms } = useContext(DashboardContext);

  // Create stable query key based on chart parameters
  const queryKey = useMemo(() => {
    if (!selectedAccount || !analyticsRange) return null;

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    return [
      'overview-analytics',
      selectedAccount.id,
      definition.id,
      startDate,
      endDate,
      definition.metrics.join(','),
      definition.segment || 'none',
      definition.comparison ? 'with-comparison' : 'no-comparison',
      campaignId || 'all-campaigns',
      // Include filter rules in query key for proper cache invalidation
      JSON.stringify(analyticsFilters.rules),
    ];
  }, [
    selectedAccount,
    analyticsRange,
    definition,
    campaignId,
    analyticsFilters,
  ]);

  const {
    data,
    isLoading: queryIsLoading,
    error,
  } = useQuery({
    queryKey: queryKey!,
    queryFn: async () => {
      if (!selectedAccount || !analyticsRange) {
        throw new Error('Missing required parameters');
      }

      const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

      // Check if this chart requires ad spend data
      const hasAdSpend = definition.metrics.includes('ad_spend');
      const hasOtherMetrics = definition.metrics.some((m) => m !== 'ad_spend');

      // If only ad_spend is needed, use getAdsAccountsUsage
      if (hasAdSpend && !hasOtherMetrics) {
        // Calculate previous period for comparison
        let prevStartStr: string | undefined;
        let prevEndStr: string | undefined;
        if (definition.comparison) {
          const daysDiff = Math.ceil(
            (new Date(endDate).getTime() - new Date(startDate).getTime()) /
              (1000 * 60 * 60 * 24),
          );
          const prevEndDate = new Date(startDate);
          prevEndDate.setDate(prevEndDate.getDate() - 1);
          const prevStartDate = new Date(prevEndDate);
          prevStartDate.setDate(prevStartDate.getDate() - daysDiff);

          prevEndStr = prevEndDate.toISOString().split('T')[0];
          prevStartStr = prevStartDate.toISOString().split('T')[0];
        }

        const aggregateBy = definition.segment === 'day' ? 'date' : 'total';

        const [currentUsage, prevUsage] = await Promise.all([
          safeGetAdsAccountsUsage(
            selectedAccount.id,
            startDate,
            endDate,
            aggregateBy,
          ),
          prevStartStr && prevEndStr
            ? safeGetAdsAccountsUsage(
                selectedAccount.id,
                prevStartStr,
                prevEndStr,
                aggregateBy,
              )
            : Promise.resolve(undefined),
        ]);

        return {
          current: currentUsage,
          previous: prevUsage,
          isAdSpend: true,
        };
      }

      // Determine groupFields and events based on chart kind and segment
      const groupFields = definition.segment ? [definition.segment] : [];

      const events = new Set<string>();

      definition.metrics.forEach((metric) => {
        switch (metric) {
          case 'visitors':
            events.add('view');
            break;
          case 'conversions':
            events.add('conversion');
            break;
          case 'cvr':
            events.add('view');
            events.add('conversion');
            break;
          case 'leads':
            events.add('form-submission');
            break;
          case 'ad_spend':
            // Skip - will be fetched separately if needed
            break;
          default:
            throw new Error(`Unsupported metric: ${metric}`);
        }
      });

      // For comparison, calculate previous period
      let prevStartStr: string | undefined;
      let prevEndStr: string | undefined;

      if (definition.comparison) {
        const daysDiff = Math.ceil(
          (new Date(endDate).getTime() - new Date(startDate).getTime()) /
            (1000 * 60 * 60 * 24),
        );
        const prevEndDate = new Date(startDate);
        prevEndDate.setDate(prevEndDate.getDate() - 1);
        const prevStartDate = new Date(prevEndDate);
        prevStartDate.setDate(prevStartDate.getDate() - daysDiff);

        prevEndStr = prevEndDate.toISOString().split('T')[0];
        prevStartStr = prevStartDate.toISOString().split('T')[0];
      }

      // Merge campaignId filter with analyticsFilters if present
      const filters = campaignId
        ? {
            ...analyticsFilters,
            rules: [
              ...(analyticsFilters.rules || []),
              {
                id: 'campaign-filter',
                field: 'campaignId' as const,
                operator: 'is' as const,
                value: campaignId,
              },
            ],
          }
        : analyticsFilters;

      // Use the new queryAnalytics API with batching
      const results = await queryAnalytics({
        accountId: selectedAccount.id,
        // Shared params
        groupBy: groupFields,
        events: Array.from(events),
        filters,
        // Batch queries - current and optionally previous period
        queries: [
          { id: 'current', startDate, endDate },
          ...(definition.comparison && prevStartStr && prevEndStr
            ? [{ id: 'previous', startDate: prevStartStr, endDate: prevEndStr }]
            : []),
        ],
      });

      // Convert array response to object for easier access
      const resultsMap = Object.fromEntries(
        results.map((r: { id: string; data: unknown }) => [r.id, r.data]),
      );

      return {
        current: resultsMap.current,
        previous: resultsMap.previous,
        isAdSpend: false,
      };
    },
    enabled: !!queryKey,
    staleTime: 3 * 60 * 1000, // 3 minutes - data is considered fresh
    gcTime: 4 * 60 * 1000, // 4 minutes - cache persists for 4 minutes
    retry: 2,
    retryDelay: 1000,
  });

  // Default to loading state if queryKey is not ready or if query is loading
  const isLoading = !queryKey || queryIsLoading;

  // Translation layer for IDs to human-readable names
  const translateSegmentLabel = useCallback(
    (segment: string | undefined, value: string): string => {
      if (!segment) return value;

      // Translate campaignId to campaign name (dynamic page name)
      if (segment === 'campaignId' || segment === 'campaign') {
        const campaign = dynamicPages?.find((page) => page.id === value);
        return campaign?.data().name || value;
      }

      // Translate formId to form name
      if (segment === 'formId' || segment === 'form') {
        const form = forms?.find((f) => f.id === value);
        return form?.data().name || value;
      }

      // Translate country code to flag + name
      if (segment === 'country') {
        return formatCountryLabel(value);
      }

      if (extraTranslateSegmentLabel) {
        return extraTranslateSegmentLabel(segment, value);
      }

      return value;
    },
    [dynamicPages, forms, extraTranslateSegmentLabel],
  );

  if (!data && !isLoading && !error) {
    return null;
  }

  // Render based on chart kind
  switch (definition.kind) {
    case 'metric':
      return renderMetricCard();
    case 'line':
      return renderLineChart();
    case 'area':
      return renderAreaChart();
    case 'bar':
      return renderBarChart();
    case 'bar-horizontal':
      return renderBarChart(true);
    case 'pie':
      return renderPieChart();
    default:
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-500">
            Chart type &quot;{definition.kind}&quot; not implemented yet
          </p>
        </div>
      );
  }

  function renderMetricCard() {
    const metric = definition.metrics[0];
    const currentValue = extractMetricValue(data?.current, metric);
    const previousValue = data?.previous
      ? extractMetricValue(data.previous, metric)
      : undefined;

    const format =
      metric === 'cvr'
        ? 'percentage'
        : metric === 'ad_spend'
          ? 'currency'
          : 'number';

    const chart = (
      <MetricCard
        value={currentValue}
        previousValue={previousValue}
        format={format}
      />
    );

    if (withoutCard) {
      return chart;
    }

    return (
      <ChartCard
        title={definition.title}
        loading={isLoading}
        error={error ? (error as Error).message : undefined}
        className={chartClassName}
      >
        {chart}
      </ChartCard>
    );
  }

  function renderLineChart() {
    const metric = definition.metrics[0];
    const chartData = extractTimeSeriesData(
      data?.current,
      metric,
      definition.segment,
    );

    const chart = (
      <LineChart
        data={chartData}
        format={
          metric === 'cvr'
            ? 'percentage'
            : metric === 'ad_spend'
              ? 'currency'
              : 'number'
        }
        loading={withoutCard ? isLoading : undefined}
        error={withoutCard && error ? (error as Error).message : undefined}
        fillParent={true}
      />
    );

    if (withoutCard) {
      return chart;
    }

    return (
      <ChartCard
        title={definition.title}
        loading={isLoading}
        error={error ? (error as Error).message : undefined}
        className={chartClassName}
      >
        {chart}
      </ChartCard>
    );
  }

  function renderAreaChart() {
    const metric = definition.metrics[0];
    const timeSeriesData = extractTimeSeriesData(
      data?.current,
      metric,
      definition.segment,
    );

    const chart = (
      <AreaChart
        data={timeSeriesData}
        format={
          metric === 'cvr'
            ? 'percentage'
            : metric === 'ad_spend'
              ? 'currency'
              : 'number'
        }
        loading={withoutCard ? isLoading : undefined}
        error={withoutCard && error ? (error as Error).message : undefined}
        fillParent={true}
      />
    );

    if (withoutCard) {
      return chart;
    }

    return (
      <ChartCard
        title={definition.title}
        loading={isLoading}
        error={error ? (error as Error).message : undefined}
        className={chartClassName}
      >
        {chart}
      </ChartCard>
    );
  }

  function renderBarChart(isHorizontal = false) {
    const metric = definition.metrics[0];
    const segment = definition.segment;

    let chartData;

    // Check if it's time series (day segment) or categorical (page, device, etc.)
    if (segment === 'day') {
      const timeSeriesData = extractTimeSeriesData(
        data?.current,
        metric,
        segment,
      );
      chartData = timeSeriesData.map((item) => ({
        label: item.date,
        value: item.value,
      }));
    } else {
      // For categorical segments (page, referrer, ad_network, etc.)
      chartData = extractSegmentData(data?.current, metric, segment);
    }

    const formatType =
      metric === 'cvr'
        ? 'percentage'
        : metric === 'ad_spend'
          ? 'currency'
          : 'number';

    const chart = isHorizontal ? (
      <HorizontalBarChart
        data={chartData}
        format={formatType}
        loading={withoutCard ? isLoading : undefined}
        error={withoutCard && error ? (error as Error).message : undefined}
        fillParent={true}
        style={{
          maxHeight:
            chartData.length < 5
              ? 32 * chartData.length + 10 * (chartData.length - 1) + 18
              : undefined,
        }}
      />
    ) : (
      <VerticalBarChart
        data={chartData}
        format={formatType}
        loading={withoutCard ? isLoading : undefined}
        error={withoutCard && error ? (error as Error).message : undefined}
        fillParent={true}
      />
    );

    if (withoutCard) {
      return chart;
    }

    return (
      <ChartCard
        title={definition.title}
        loading={isLoading}
        error={error ? (error as Error).message : undefined}
        className={chartClassName}
      >
        {chart}
      </ChartCard>
    );
  }

  function renderPieChart() {
    const metric = definition.metrics[0];
    const chartData = extractSegmentData(
      data?.current,
      metric,
      definition.segment,
    );

    const chart = (
      <PieChart
        legend={{ position: 'right' }}
        data={chartData}
        minLegendItems={definition.minLegendItems}
        loading={withoutCard ? isLoading : undefined}
        error={withoutCard && error ? (error as Error).message : undefined}
        fillParent={!withoutCard}
      />
    );

    if (withoutCard) {
      return chart;
    }

    return (
      <ChartCard
        title={definition.title}
        loading={isLoading}
        error={error ? (error as Error).message : undefined}
        className={chartClassName}
      >
        {chart}
      </ChartCard>
    );
  }

  function extractMetricValue(analyticsData: any, metric: string): number {
    if (!analyticsData || analyticsData.length === 0) return 0;

    // Handle ad spend data differently
    if (metric === 'ad_spend' && data?.isAdSpend) {
      // Sum up the cost from ads account usage data
      return analyticsData.reduce(
        (sum: number, item: any) => sum + (item.cost || 0),
        0,
      );
    }

    // Aggregate data for analytics metrics
    const totals = analyticsData.reduce((acc: any, item: any) => {
      if (item.event === 'view') {
        acc.visitors = (acc.visitors || 0) + (item.custom || 0);
      } else if (item.event === 'conversion') {
        acc.conversions = (acc.conversions || 0) + (item.custom || 0);
      } else if (item.event === 'form-submission') {
        acc.leads = (acc.leads || 0) + (item.custom || 0);
      }
      return acc;
    }, {});

    switch (metric) {
      case 'visitors':
        return totals.visitors || 0;
      case 'conversions':
        return totals.conversions || 0;
      case 'cvr': {
        const visitors = totals.visitors || 0;
        const conversions = totals.conversions || 0;
        return visitors > 0 ? (conversions / visitors) * 100 : 0;
      }
      case 'leads':
        return totals.leads || 0;
      case 'ad_spend':
        return 0;
      default:
        return 0;
    }
  }

  function extractTimeSeriesData(
    analyticsData: any,
    metric: string,
    segment?: string,
  ): Array<{ date: string; value: number }> {
    if (!analyticsData || !segment || segment !== 'day') return [];

    // Handle ad spend data differently
    if (metric === 'ad_spend' && data?.isAdSpend) {
      // analyticsData is already aggregated by date
      return analyticsData
        .map((item: any) => ({
          date: item.date,
          value: item.cost || 0,
        }))
        .sort((a: any, b: any) => a.date.localeCompare(b.date));
    }

    // Group by day for analytics metrics
    const dailyData = analyticsData.reduce((acc: any, item: any) => {
      if (!item.day) return acc;

      if (!acc[item.day]) {
        acc[item.day] = { visitors: 0, conversions: 0, leads: 0 };
      }

      if (item.event === 'view') {
        acc[item.day].visitors += item.custom || 0;
      } else if (item.event === 'conversion') {
        acc[item.day].conversions += item.custom || 0;
      } else if (item.event === 'form-submission') {
        acc[item.day].leads += item.custom || 0;
      }

      return acc;
    }, {});

    // Convert to array and calculate metric values
    return Object.entries(dailyData)
      .map(([day, data]: [string, any]) => {
        let value = 0;

        switch (metric) {
          case 'visitors':
            value = data.visitors;
            break;
          case 'conversions':
            value = data.conversions;
            break;
          case 'cvr':
            value =
              data.visitors > 0 ? (data.conversions / data.visitors) * 100 : 0;
            break;
          case 'leads':
            value = data.leads;
            break;
        }

        return { date: day, value };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  function extractSegmentData(
    analyticsData: any,
    _metric: string,
    segment?: string,
  ): Array<{ label: string; value: number }> {
    if (!analyticsData || !segment) return [];

    // For device/source segments, group by that field
    const segmentData: Record<string, number> = {};

    analyticsData.forEach((item: any) => {
      if (item.event !== 'view') return; // Only count visitors

      const segmentValue = item[segment];
      if (!segmentValue) return;

      segmentData[segmentValue] =
        (segmentData[segmentValue] || 0) + (item.custom || 0);
    });

    // Convert to array, translate labels, sort by value
    return Object.entries(segmentData)
      .map(([id, value]) => ({
        label: translateSegmentLabel(segment, id),
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Top 5 segments
  }
}
